import { Button, Card, ErrorSummary } from '@beautyburst/ui';
import { semanticColor, space, typeStyle } from '@beautyburst/tokens';
import { customerHref } from '../base-path.ts';
import { useState } from 'react';
import type { FormEvent } from 'react';
import type { AuthOutcome, CustomerApiClient } from '../api-client.ts';
import { GENERIC_AUTH_FAILURE } from '../api-client.ts';
import { errorsForSummary, FormField, useFormErrors, validateEmail } from '../forms.tsx';

export interface SignInScreenProps {
  client: { readonly auth: Pick<CustomerApiClient['auth'], 'signIn'> };
  /** Destination requested by a protected customer route. */
  returnTo?: string;
  onSignedIn?: (destination: string, outcome: Extract<AuthOutcome, { ok: true }>) => void;
}

export function SignInScreen({ client, returnTo = '/', onSignedIn }: SignInScreenProps) {
  const { errors, setErrors, clear } = useFormErrors();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const emailError = validateEmail(email);
    if (emailError !== undefined || password.length === 0) {
      setErrors({
        ...(emailError === undefined ? {} : { 'sign-in-email': emailError }),
        ...(password.length === 0 ? { 'sign-in-password': 'Enter your password.' } : {}),
      });
      return;
    }

    setPending(true);
    try {
      const outcome = await client.auth.signIn({ email: email.trim(), password });
      if (outcome.ok) {
        clear();
        onSignedIn?.(returnTo, outcome);
        return;
      }
      // The service message is intentionally ignored so the UI cannot distinguish failure causes.
      setErrors({ 'sign-in-email': GENERIC_AUTH_FAILURE });
    } catch {
      setErrors({ 'sign-in-email': GENERIC_AUTH_FAILURE });
    } finally {
      setPending(false);
    }
  };

  return (
    <section
      aria-labelledby="sign-in-heading"
      style={{ maxWidth: '32rem', margin: '0 auto', padding: space[6] }}
    >
      <h1
        id="sign-in-heading"
        style={{ ...typeStyle('headingLg'), color: semanticColor.textPrimary, margin: 0 }}
      >
        Sign in to Beauty Burst
      </h1>
      <p
        style={{
          ...typeStyle('body'),
          color: semanticColor.textSecondary,
          margin: `${space[2]} 0 ${space[5]}`,
        }}
      >
        Continue shopping beauty products from local stores.
      </p>
      <Card>
        <ErrorSummary errors={errorsForSummary(errors)} />
        <form noValidate onSubmit={submit} style={{ display: 'grid', gap: space[4] }}>
          <FormField
            id="sign-in-email"
            label="Email address"
            error={errors['sign-in-email']}
            required
          >
            <input
              type="email"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(event) => {
                setEmail(event.currentTarget.value);
                clear('sign-in-email');
              }}
            />
          </FormField>
          <FormField
            id="sign-in-password"
            label="Password"
            error={errors['sign-in-password']}
            required
          >
            <input
              type="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => {
                setPassword(event.currentTarget.value);
                clear('sign-in-password');
              }}
            />
          </FormField>
          <Button type="submit" disabled={pending} aria-label={pending ? 'Signing in…' : 'Sign in'}>
            {pending ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
        <p
          style={{
            ...typeStyle('bodySm'),
            color: semanticColor.textSecondary,
            margin: `${space[4]} 0 0`,
          }}
        >
          <a href={customerHref('/forgot-password')} style={{ color: semanticColor.textLink }}>
            Forgot password?
          </a>
        </p>
        <p
          style={{
            ...typeStyle('bodySm'),
            color: semanticColor.textSecondary,
            margin: `${space[2]} 0 0`,
          }}
        >
          New to Beauty Burst?{' '}
          <a href={customerHref('/sign-up')} style={{ color: semanticColor.textLink }}>
            Create an account
          </a>
          .
        </p>
      </Card>
    </section>
  );
}
